import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Phone, 
  MessageSquare, 
  Mail, 
  Share2, 
  Search, 
  Linkedin, 
  FileText,
  Bot,
  Users,
  Instagram,
  Facebook,
  PenTool,
  MessageCircle,
  Eye,
  TrendingUp,
  Zap,
  ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const agents = [
  {
    icon: Phone,
    title: 'Standard Telephonique IA',
    description: 'Repond a tous tes appels, qualifie les demandes, identifie les urgences et prend les rendez-vous automatiquement.',
    highlights: ['Disponible 24h/24', 'Qualification des prospects', 'Transfert des urgences', 'Prise de RDV automatique'],
    color: 'bg-blue-500',
  },
  {
    icon: Mail,
    title: 'Gestion Boite Mail',
    description: 'Trie, analyse et repond automatiquement a tes emails. Signale les priorites et gere les demandes courantes.',
    highlights: ['Tri intelligent', 'Reponses automatiques', 'Alertes prioritaires', 'Suivi des relances'],
    color: 'bg-purple-500',
  },
  {
    icon: MessageSquare,
    title: 'Service Client IA',
    description: 'Gere tes messages sur la messagerie BatiAssist, SMS et reseaux sociaux. Reponse instantanee a tes clients.',
    highlights: ['Messagerie BatiAssist', 'SMS automatises', 'Messages prives Instagram', 'Messages prives Facebook'],
    color: 'bg-emerald-500',
  },
];

const marketingFeatures = [
  {
    icon: PenTool,
    title: 'Creation de contenu',
    description: 'Crée des visuels professionnels, rédige des textes accrocheurs et programme tes publications.',
  },
  {
    icon: MessageCircle,
    title: 'Commentaires & Reponses',
    description: 'Commente sur les posts pertinents, repond aux commentaires et engage ta communauté.',
  },
  {
    icon: Eye,
    title: 'Visibilite & Croissance',
    description: 'Developpe ta présence en ligne, augmente tes abonnés et genere des leads qualifies.',
  },
  {
    icon: Search,
    title: 'SEO & Blog',
    description: 'Optimise ton référencement Google, redige des articles de blog et améliore ta visibilité.',
  },
];

const prospectionFeatures = [
  {
    icon: Linkedin,
    title: 'Prospection LinkedIn',
    description: 'Identifie les prospects, envoie des messages personnalises et developpe ton réseau professionnel.',
  },
  {
    icon: Users,
    title: 'Qualification',
    description: 'Analyse les profils, qualifie les opportunites et prepare les rendez-vous commerciaux.',
  },
  {
    icon: FileText,
    title: 'Administratif',
    description: 'Rédige tes documents, prépare tes dossiers et gère les tâches administratives répétitives.',
  },
  {
    icon: TrendingUp,
    title: 'Reporting',
    description: 'Suit tes performances, génère des rapports et analyse tes résultats marketing.',
  },
];

export default function AgentsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-white"
      data-testid="agents-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
            <Bot className="w-4 h-4" />
            Equipe Digitale
          </span>
          <h2 className="heading-section mb-6">
            7 agents IA specialises{' '}
            <span className="text-gradient">a ton service</span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Une equipe digitale complete qui travaille pour toi 24h/24, 7j/7. 
            Standard telephonique, gestion emails, marketing digital, prospection...
          </p>
        </motion.div>

        {/* Main Agents - Communication */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 bg-ba-primary/10 rounded-xl flex items-center justify-center">
              <Phone className="w-6 h-6 text-ba-primary" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-slate-800">Communication & Service Client</h3>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {agents.map((agent, index) => {
              const Icon = agent.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="card-premium bg-white rounded-2xl p-6 md:p-8"
                >
                  <div className={`w-16 h-16 ${agent.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-slate-800 mb-3">{agent.title}</h4>
                  <p className="text-slate-500 mb-6">{agent.description}</p>
                  <div className="space-y-3">
                    {agent.highlights.map((highlight, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <Zap className="w-4 h-4 text-ba-secondary flex-shrink-0" />
                        <span className="text-slate-600">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Marketing Digital Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mb-20 bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 md:p-12 border border-pink-100/50"
        >
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Share2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800">Marketing Digital Automatise</h3>
              <p className="text-slate-500">Tes réseaux sociaux gérés par l'IA</p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {marketingFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-pink-100 shadow-sm"
                >
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-pink-600" />
                  </div>
                  <h4 className="font-bold text-slate-800 mb-2">{feature.title}</h4>
                  <p className="text-slate-500 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-pink-100 shadow-sm">
              <Instagram className="w-5 h-5 text-pink-500" />
              <span className="text-sm font-semibold text-slate-700">Instagram</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-pink-100 shadow-sm">
              <Facebook className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">Facebook</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-pink-100 shadow-sm">
              <Linkedin className="w-5 h-5 text-blue-700" />
              <span className="text-sm font-semibold text-slate-700">LinkedIn</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-pink-100 shadow-sm">
              <Search className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-slate-700">Google SEO</span>
            </div>
          </div>
        </motion.div>

        {/* Prospection & Admin Section */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.7 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 bg-ba-primary/10 rounded-xl flex items-center justify-center">
              <Linkedin className="w-6 h-6 text-ba-primary" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-slate-800">Prospection & Administratif</h3>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {prospectionFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="card-premium bg-slate-50 rounded-2xl p-6"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-slate-100">
                    <Icon className="w-6 h-6 text-ba-primary" />
                  </div>
                  <h4 className="font-bold text-slate-800 mb-2">{feature.title}</h4>
                  <p className="text-slate-500 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="text-center"
        >
          <a 
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button 
              className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-10 py-7 text-lg btn-premium group"
              data-testid="agents-cta"
            >
              Decouvrir l'equipe digitale
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
